import {
  ATTACH_KINDS,
  bodySlotsGrid,
  canPlace,
  containerAccepts,
  contentsGridFor,
  EQUIP_SLOTS,
  WEAPON_EQUIP_SLOTS,
  canEquipInSlot,
  fittedMagazine,
  isTrueMagazine,
  isWeaponEquipSlot,
  findFreePlacement,
  findModHost,
  findNestedItem,
  footprint,
  getItemDef,
  GRID_AREAS,
  MAX_GRID_DEPTH,
  subtreeDepth,
  type AmmoItemDef,
  type AreaId,
  type AttachKind,
  type AttachmentItemDef,
  type ContainerItemDef,
  type EquipSlot,
  type GridState,
  type Inventory,
  type InventoryOp,
  type ItemDef,
  type ItemInstance,
  type PlacedItem,
  type Rotation,
  type TargetRef,
} from "@rietz/shared";
import type { OpenContainer } from "../store.js";

/**
 * The drag-and-drop model, with no React in it: plain functions over plain
 * state answering "where would this land, and is that legal?". The rules come
 * from `@rietz/shared` (`canPlace`, `findFreePlacement`) — the very functions
 * the server runs; the UI only asks the right question, passing `ignoreIid`
 * exactly when the dragged item is among the obstacles.
 */

/** Side of one grid cell in CSS pixels. The whole screen is laid out off this. */
export const CELL = 44;

/**
 * Converts a viewport point into `#ui`'s coordinate space (the root may be
 * scaled).
 */
export function viewportToUi(clientX: number, clientY: number): { x: number; y: number } {
  if (typeof document === "undefined") return { x: clientX, y: clientY };
  const root = document.getElementById("ui");
  if (root === null || root.clientWidth <= 0 || root.clientHeight <= 0) {
    return { x: clientX, y: clientY };
  }
  const rect = root.getBoundingClientRect();
  const scaleX = rect.width / root.clientWidth;
  const scaleY = rect.height / root.clientHeight;
  return {
    x: scaleX > 0 ? (clientX - rect.left) / scaleX : clientX,
    y: scaleY > 0 ? (clientY - rect.top) / scaleY : clientY,
  };
}

/** Logical viewport size for overlays rendered inside the transformed UI. */
export function uiViewportSize(): { width: number; height: number } {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return { width: Number.MAX_SAFE_INTEGER, height: Number.MAX_SAFE_INTEGER };
  }
  const root = document.getElementById("ui");
  return root === null
    ? { width: window.innerWidth, height: window.innerHeight }
    : { width: root.clientWidth, height: root.clientHeight };
}

/** Where an item currently lives — the places a drag can start. */
export type ItemSource =
  | { t: "grid"; area: AreaId }
  | { t: "slot"; slot: EquipSlot }
  /**
   * A fitted attachment pulled off a weapon on the bench. `weaponIid` is the
   * gun (the only part the inventory can find); `hostIid` names the host inside
   * it, absent = the gun.
   */
  | { t: "mod"; weaponIid: string; slot: AttachKind; hostIid?: string }
  /**
   * A cell of an open box's contents grid, named by the host's iid (a nested
   * grid has no fixed address).
   */
  | { t: "box"; boxIid: string }
  /**
   * A slot of the open body's equipment doll — take-only, unlike `slot`, so
   * every "may this land here" rule can tell them apart.
   */
  | { t: "bodySlot"; slot: EquipSlot };

/**
 * Where the pointer is, resolved against the rules. `ok` colours the highlight
 * and is computed on every move, not on release.
 */
export type DropHover =
  | {
      t: "cell";
      area: AreaId;
      /** Never set for a top-level area; see the `"inside"` variant below. */
      boxIid?: undefined;
      /** The item's TOP-LEFT cell, which may be out of bounds (then `ok` is false). */
      x: number;
      y: number;
      ok: boolean;
      /** Set when the drop would top up an existing stack instead of moving. */
      ontoIid: string | null;
    }
  /** A cell of a nested contents grid. `area` doubles as the discriminant, so
   *  `boxIid` is known to be present exactly when it is meaningful. */
  | {
      t: "cell";
      area: "inside";
      boxIid: string;
      x: number;
      y: number;
      ok: boolean;
      ontoIid: string | null;
    }
  | { t: "slot"; slot: EquipSlot; ok: boolean }
  /** A part going onto a gun. `hostIid` reads as it does on the `mod` source:
   *  absent is the gun itself, present is a part already fitted to it. */
  | { t: "weapon"; weaponIid: string; slot: AttachKind | null; hostIid?: string; ok: boolean }
  /**
   * Pouring carried cartridges into a gun or a magazine in a grid. Shaped like
   * `merge`: nothing lands in a cell.
   */
  | { t: "load"; ontoIid: string; ok: boolean }
  /** Dropping on a container TILE auto-places into its first legal cell. */
  | { t: "container"; containerIid: string; to: TargetRef | null; ok: boolean }
  /**
   * A screen-specific sink (the trader's sell counter); the hook that
   * registered it decides acceptance.
   */
  | { t: "action"; action: string; ok: boolean };

export interface DragState {
  /** The pointer that owns this drag; every other pointer's events are ignored. */
  pointerId: number;
  iid: string;
  defId: string;
  from: ItemSource;
  /**
   * Where it sat when the drag began — read only to recognise a drop back where
   * it was.
   */
  origin: { x: number; y: number; rot: Rotation };
  /** Starts at the item's placed rotation; R toggles it. */
  rot: Rotation;
  /** Cursor in viewport pixels — where the ghost is drawn. */
  px: number;
  py: number;
  /**
   * The anchor under the cursor, in pixels from the top-left: always the CENTRE
   * of the footprint, so a 1x3 lands with its middle cell under the pointer.
   */
  grabX: number;
  grabY: number;
  hover: DropHover | null;
}

// ---------------------------------------------------------------------------
// Lookups over the two grids-bearing objects the screen renders
// ---------------------------------------------------------------------------

/** The grid behind an area id, or `null` when nothing is worn / open there. */
export function gridOfArea(
  inv: Inventory,
  container: OpenContainer | null,
  area: AreaId,
): GridState | null {
  switch (area) {
    case "pockets":
      return inv.pockets;
    case "rig":
      return inv.rig;
    case "backpack":
      return inv.backpack;
    case "container":
      return container === null ? null : container.grid;
  }
}

/**
 * Every top-level grid the screen can reach, canonical order, open container
 * (or stash) last — the depth-0 roots `findNestedItem` walks from. Equipment
 * slots are not among them: a worn rig's contents are hoisted onto `inv.rig`.
 */
export function gridRoots(
  inv: Inventory,
  container: OpenContainer | null,
): { area: AreaId; grid: GridState }[] {
  const roots: { area: AreaId; grid: GridState }[] = [];
  for (const area of GRID_AREAS) {
    const grid = gridOfArea(inv, container, area);
    if (grid !== null) roots.push({ area, grid });
  }
  if (container !== null) roots.push({ area: "container", grid: container.grid });
  return roots;
}

/**
 * A nested container and its grid, resolved wherever the box has got to.
 * `depth` is the CONTENTS grid's depth; `chain` runs outermost-in, ending with
 * the box.
 */
export interface BoxTarget {
  def: ContainerItemDef;
  item: ItemInstance;
  grid: GridState;
  depth: number;
  chain: readonly string[];
  /** Which top-level area the box's outermost host sits in, or `"body"` for one
   *  worn by (or stowed on) the corpse being looted — see `nestingRoots`. */
  area: BoxArea;
}

/**
 * Where a nested grid hangs off: an area, the open container, or the body's
 * doll (not an area — nothing may be placed into it).
 */
export type BoxArea = AreaId | "body";

/**
 * Every grid a LOOKUP may descend through, the body's slots last. `gridRoots`
 * answers "where may something be put"; this answers "where might something
 * be". The body joins as the ops layer's one-row pseudo-grid, in the ops' own
 * search order, so a hover and its op never name two items.
 */
function searchRoots(
  inv: Inventory,
  container: OpenContainer | null,
): { area: BoxArea; grid: GridState }[] {
  const roots: { area: BoxArea; grid: GridState }[] = gridRoots(inv, container);
  const equipment = container?.equipment;
  if (equipment !== undefined) roots.push({ area: "body", grid: bodySlotsGrid(equipment) });
  return roots;
}

/** `searchRoots` without the labels — the roots `findNestedItem` wants. */
export function nestingRoots(inv: Inventory, container: OpenContainer | null): GridState[] {
  return searchRoots(inv, container).map((root) => root.grid);
}

/**
 * Finds box `boxIid` anywhere on screen. `null` when unreachable or not a
 * container — how a sold or dropped box closes its own panel.
 */
export function boxAt(
  inv: Inventory,
  container: OpenContainer | null,
  boxIid: string,
): BoxTarget | null {
  for (const root of searchRoots(inv, container)) {
    const hit = findNestedItem([root.grid], boxIid);
    if (hit === null) continue;
    const def = getItemDef(hit.item.defId);
    if (def === null || def.kind !== "container") return null;
    return {
      def,
      item: hit.item,
      grid: contentsGridFor(def, hit.item),
      depth: hit.depth + 1,
      chain: [...hit.chain, boxIid],
      area: root.area,
    };
  }
  return null;
}

/** Footprint of an item already placed. Unknown defs are 1x1, as in `grid.ts`. */
export function placedSize(placed: PlacedItem): { w: number; h: number } {
  const def = getItemDef(placed.item.defId);
  return def === null ? { w: 1, h: 1 } : footprint(def, placed.rot);
}

/** Whatever covers cell (x, y), or `null`. The merge test is built on this. */
export function itemAtCell(grid: GridState, x: number, y: number): PlacedItem | null {
  for (const p of grid.items) {
    const f = placedSize(p);
    if (x >= p.x && x < p.x + f.w && y >= p.y && y < p.y + f.h) return p;
  }
  return null;
}

/** The instance a source refers to, wherever it lives. */
export function instanceAt(
  inv: Inventory,
  container: OpenContainer | null,
  source: ItemSource,
  iid: string,
): ItemInstance | null {
  if (source.t === "mod") {
    const weapon = findReachableInstance(inv, container, source.weaponIid);
    if (weapon === null) return null;
    // Which host the slot is on: the gun for a bare `mod`, else the part
    // `hostIid` names via the shared `findModHost`.
    const host = modHostOf(weapon, source.hostIid);
    const mod = host?.mods?.[source.slot];
    return mod !== undefined && mod.iid === iid ? mod : null;
  }
  if (source.t === "slot") {
    const item = inv.equipment[source.slot];
    return item !== null && item.iid === iid ? item : null;
  }
  if (source.t === "bodySlot") {
    const item = container?.equipment?.[source.slot] ?? null;
    return item !== null && item.iid === iid ? item : null;
  }
  const grid =
    source.t === "box"
      ? boxAt(inv, container, source.boxIid)?.grid ?? null
      : gridOfArea(inv, container, source.area);
  if (grid === null) return null;
  for (const p of grid.items) {
    if (p.item.iid === iid) return p.item;
  }
  return null;
}

/**
 * The instance `iid` names anywhere reachable — a weapon slot or any grid at
 * any depth. Kind-agnostic; the ops' own search order.
 */
function findReachableInstance(
  inv: Inventory,
  container: OpenContainer | null,
  iid: string,
): ItemInstance | null {
  for (const slot of WEAPON_EQUIP_SLOTS) {
    const equipped = inv.equipment[slot];
    if (equipped !== null && equipped.iid === iid) return equipped;
  }
  for (const grid of nestingRoots(inv, container)) {
    const found = findNestedItem([grid], iid);
    if (found !== null) return found.item;
  }
  return null;
}

/**
 * The instance whose `mods` a bench gesture is about: the gun for
 * `undefined`/its own iid, else the part via the shared `findModHost`.
 */
function modHostOf(weapon: ItemInstance, hostIid: string | undefined): ItemInstance | null {
  if (hostIid === undefined || hostIid === weapon.iid) return weapon;
  const def = getItemDef(weapon.defId);
  if (def === null || def.kind !== "weapon") return null;
  return findModHost(def, weapon, hostIid)?.item ?? null;
}

// A full bag can come off since v9 (it wraps its contents), so the only thing
// it needs is a free spot of its own footprint.

// ---------------------------------------------------------------------------
// Hit testing
// ---------------------------------------------------------------------------

const AREA_IDS: readonly AreaId[] = ["pockets", "rig", "backpack", "container"];

function toArea(raw: string | null): AreaId | null {
  return AREA_IDS.find((a) => a === raw) ?? null;
}

function toSlot(raw: string | null): EquipSlot | null {
  return EQUIP_SLOTS.find((s) => s === raw) ?? null;
}

/** The weapon tile/model under a viewport point, if there is one. */
export function weaponAt(clientX: number, clientY: number): string | null {
  return weaponTargetAt(clientX, clientY)?.weaponIid ?? null;
}

/**
 * A whole-weapon target, or a bench slot inside it. The slot element carries
 * its host (`data-weapon-host`); without it the slot is the gun's own.
 */
export function weaponTargetAt(
  clientX: number,
  clientY: number,
): { weaponIid: string; slot: AttachKind | null; hostIid?: string } | null {
  const el = document.elementFromPoint(clientX, clientY);
  const weaponEl = el?.closest("[data-weapon-iid]") ?? null;
  const iid = weaponEl?.getAttribute("data-weapon-iid") ?? "";
  if (iid.length === 0) return null;
  const slotEl = el?.closest("[data-weapon-slot]") ?? null;
  const rawSlot = slotEl?.getAttribute("data-weapon-slot") ?? "";
  const slot = ATTACH_KINDS.find((kind) => kind === rawSlot) ?? null;
  const rawHost = slotEl?.getAttribute("data-weapon-host") ?? "";
  return rawHost.length === 0 || rawHost === iid
    ? { weaponIid: iid, slot }
    : { weaponIid: iid, slot, hostIid: rawHost };
}

/** The loose or equipped container tile under a viewport point. */
export function containerTargetAt(clientX: number, clientY: number): string | null {
  const el = document.elementFromPoint(clientX, clientY);
  const containerEl = el?.closest("[data-container-iid]") ?? null;
  const iid = containerEl?.getAttribute("data-container-iid") ?? "";
  return iid.length === 0 ? null : iid;
}

/**
 * The quick-fit gesture: carry a loose attachment over a reachable weapon and
 * release. Compatibility is the host def's whitelist; an occupied slot is an
 * invalid target rather than a silent replace. Dropped on a bench row,
 * `hostIid` is that row's host; anywhere else on the gun, the gun's own slots.
 * Nothing auto-targets a NESTED slot.
 */
export function weaponDropHover(
  inv: Inventory,
  weaponIid: string,
  draggedDef: ItemDef,
  preferredSlot: AttachKind | null = null,
  container: OpenContainer | null = null,
  hostIid?: string,
): Extract<DropHover, { t: "weapon" }> {
  // Naming the gun is naming no host, normalised so every caller produces the
  // same hover.
  const host = hostIid === undefined || hostIid === weaponIid ? undefined : hostIid;

  /** Every answer has the same shape, host and all. */
  const out = (slot: AttachKind | null, ok: boolean): Extract<DropHover, { t: "weapon" }> =>
    host === undefined
      ? { t: "weapon", weaponIid, slot, ok }
      : { t: "weapon", weaponIid, slot, hostIid: host, ok };
  const no = (slot: AttachKind | null): Extract<DropHover, { t: "weapon" }> => out(slot, false);

  let weapon: ItemInstance | null = null;
  for (const slot of WEAPON_EQUIP_SLOTS) {
    const equipped = inv.equipment[slot];
    if (equipped !== null && equipped.iid === weaponIid) weapon = equipped;
  }
  if (weapon === null) {
    for (const grid of nestingRoots(inv, container)) {
      const found = findNestedItem([grid], weaponIid);
      if (found !== null) {
        weapon = found.item;
        break;
      }
    }
  }

  if (weapon === null || draggedDef.kind !== "attachment") return no(null);
  const weaponDef = getItemDef(weapon.defId);
  if (weaponDef === null || weaponDef.kind !== "weapon") return no(null);

  // An unresolvable host is a refusal, not a fallback to the gun — putting the
  // part somewhere else is worse than not fitting it.
  const on =
    host === undefined ? { def: weaponDef, item: weapon } : findModHost(weaponDef, weapon, host);
  if (on === null) return no(null);

  const attachment = draggedDef as AttachmentItemDef;
  const slot = on.def.slots?.find((candidate) =>
    preferredSlot === null
      ? candidate.id === attachment.attachKind
      : candidate.id === preferredSlot,
  );
  return out(
    slot?.id ?? null,
    slot !== undefined &&
      slot.id === attachment.attachKind &&
      slot.accepts.includes(attachment.id) &&
      on.item.mods?.[slot.id] === undefined,
  );
}

/**
 * DRAG-TO-FILL: cartridges over a gun (tile, doll slot, bench portrait or slot
 * box) or a magazine in a grid. The op is `load`, which pours, so no cells or
 * filters. The four refusals are the op's own, in its order: caliber,
 * slot-empty (no magazine — the shotgun's tube counts), full (the fitted
 * magazine's capacity minus its rounds), def-mismatch (one cartridge per
 * magazine). `ok: false` with the target still named, so the tile can paint red.
 */
export function loadDropHover(
  inv: Inventory,
  container: OpenContainer | null,
  targetIid: string,
  ammoDef: AmmoItemDef,
): Extract<DropHover, { t: "load" }> {
  const no: Extract<DropHover, { t: "load" }> = { t: "load", ontoIid: targetIid, ok: false };
  const target = findReachableInstance(inv, container, targetIid);
  if (target === null) return no;
  const def = getItemDef(target.defId);
  if (def === null) return no;

  // A gun: every number comes off the box in it, as `fillWeapon` reads them;
  // the gun answers only for the caliber.
  if (def.kind === "weapon") {
    if (def.caliber !== ammoDef.caliber) return no;
    const fitted = fittedMagazine(def, target);
    if (fitted === null) return no;
    const have = fitted.item.mag ?? 0;
    if (fitted.def.magCapacity - have <= 0) return no;
    if (have > 0 && fitted.item.ammoDefId !== ammoDef.id) return no;
    return { t: "load", ontoIid: targetIid, ok: true };
  }

  // A loose magazine answers for itself — filling spares in the stash.
  if (isTrueMagazine(def)) {
    if (def.caliber !== ammoDef.caliber) return no;
    const have = target.mag ?? 0;
    if (def.magCapacity - have <= 0) return no;
    if (have > 0 && target.ammoDefId !== ammoDef.id) return no;
    return { t: "load", ontoIid: targetIid, ok: true };
  }

  // Located, and nothing pours into it — a scope, a tin of beans. Red, and the
  // op would say "no-slot" for the same reason.
  return no;
}

/**
 * A drop onto a bag/box tile: no cursor-addressed cell, so the canonical
 * first-free scan. Worn containers target their hoisted grid, loose ones
 * `inside`.
 */
export function containerDropHover(
  inv: Inventory,
  container: OpenContainer | null,
  containerIid: string,
  drag: DragState,
  draggedDef: ItemDef,
): Extract<DropHover, { t: "container" }> {
  const invalid = (): Extract<DropHover, { t: "container" }> => ({
    t: "container",
    containerIid,
    to: null,
    ok: false,
  });
  if (containerIid === drag.iid) return invalid();

  const dragged = instanceAt(inv, container, drag.from, drag.iid);
  if (dragged === null) return invalid();
  const subtree = subtreeDepth(wrapped(inv, drag.from, dragged));

  // Worn containers have no `contents`: their capacity is hoisted onto the
  // inventory area named by the equipment slot.
  for (const slot of ["rig", "backpack"] as const) {
    const host = inv.equipment[slot];
    if (host === null || host.iid !== containerIid) continue;
    const hostDef = getItemDef(host.defId);
    const grid = inv[slot];
    if (hostDef === null || hostDef.kind !== "container" || grid === null) return invalid();
    // It is already inside this worn container; dropping it on the bag is a
    // cancel, not a request to spend an op moving it to another arbitrary cell.
    if (drag.from.t === "grid" && drag.from.area === slot) return invalid();
    if (!containerAccepts(hostDef, draggedDef) || subtree > MAX_GRID_DEPTH) return invalid();
    const spot = findFreePlacement(grid, draggedDef);
    return spot === null
      ? invalid()
      : {
          t: "container",
          containerIid,
          to: { area: slot, x: spot.x, y: spot.y, rot: spot.rot },
          ok: true,
        };
  }

  const box = boxAt(inv, container, containerIid);
  if (box === null || (drag.from.t === "box" && drag.from.boxIid === containerIid)) {
    return invalid();
  }
  const legal =
    containerAccepts(box.def, draggedDef) &&
    box.depth + subtree <= MAX_GRID_DEPTH &&
    !box.chain.includes(drag.iid);
  if (!legal) return invalid();
  const spot = findFreePlacement(box.grid, draggedDef);
  return spot === null
    ? invalid()
    : {
        t: "container",
        containerIid,
        to: { area: "inside", iid: containerIid, x: spot.x, y: spot.y, rot: spot.rot },
        ok: true,
      };
}

/** The named non-inventory drop target at a viewport point, if there is one. */
export function actionAt(clientX: number, clientY: number): string | null {
  const el = document.elementFromPoint(clientX, clientY);
  const actionEl = el?.closest("[data-inventory-action]") ?? null;
  const action = actionEl?.getAttribute("data-inventory-action") ?? "";
  return action.length === 0 ? null : action;
}

/**
 * The drop zone under a viewport point, via `elementFromPoint` — the only
 * lookup that cannot go stale under scroll, resize or a bag taken off
 * mid-drag. Works during a drag because the ghost is `pointer-events: none`.
 */
export function zoneAt(
  clientX: number,
  clientY: number,
):
  | { t: "grid"; area: AreaId; rect: DOMRect }
  | { t: "box"; boxIid: string; rect: DOMRect }
  | { t: "slot"; slot: EquipSlot }
  | null {
  const el = document.elementFromPoint(clientX, clientY);
  if (el === null) return null;

  const gridEl = el.closest("[data-drop-area]");
  if (gridEl !== null) {
    const raw = gridEl.getAttribute("data-drop-area");
    // The box panel writes `"inside"` (the wire's word) and names its host.
    if (raw === "inside") {
      const boxIid = gridEl.getAttribute("data-box-iid") ?? "";
      if (boxIid.length > 0) {
        return { t: "box", boxIid, rect: gridEl.getBoundingClientRect() };
      }
    }
    const area = toArea(raw);
    if (area !== null) return { t: "grid", area, rect: gridEl.getBoundingClientRect() };
  }
  const slotEl = el.closest("[data-drop-slot]");
  if (slotEl !== null) {
    const slot = toSlot(slotEl.getAttribute("data-drop-slot"));
    if (slot !== null) return { t: "slot", slot };
  }
  return null;
}

/**
 * The instance AS IT WILL BE once the drag lands. A worn rig/backpack keeps
 * its contents on `Inventory.rig`/`.backpack`, so the slot instance measures a
 * subtree depth of 0; `unequip` wraps the capacity back on, and the depth cap
 * applies to the wrapped item. A body slot arrives already wrapped.
 */
function wrapped(inv: Inventory, from: ItemSource, item: ItemInstance): ItemInstance {
  if (from.t !== "slot" || (from.slot !== "rig" && from.slot !== "backpack")) return item;
  const capacity = inv[from.slot];
  // An empty capacity grid is never adopted — absent is what empty looks like.
  if (capacity === null || capacity.items.length === 0) return item;
  return { ...item, contents: capacity };
}

/**
 * Whether `target` is free for a `reslot`, or its occupant fits `source` — the
 * same question `applyInventoryOp`'s `reslot` asks.
 */
function occupantYields(inv: Inventory, target: EquipSlot, source: EquipSlot): boolean {
  const other = inv.equipment[target];
  if (other === null) return true;
  const otherDef = getItemDef(other.defId);
  return otherDef !== null && canEquipInSlot(otherDef, source);
}

/**
 * Which destinations are shut because unsearched. `true` locks the top-level
 * container area; a body has up to three places, so the caller passes a
 * predicate (`searchDropLock` in `search.ts`). The player's own grids are never asked.
 */
export type DropLock = boolean | ((where: DropWhere) => boolean);

/** A destination the lock is asked about: the open container's own grid, or the
 *  inside of one of the bags it is wearing. */
export type DropWhere = { area: "container" } | { area: "inside"; boxIid: string };

function locked(lock: DropLock, where: DropWhere): boolean {
  return typeof lock === "boolean" ? lock && where.area === "container" : lock(where);
}

/** Resolves the pointer position into a hover, rules included. */
export function computeHover(
  drag: DragState,
  def: ItemDef,
  inv: Inventory,
  container: OpenContainer | null,
  _containerOwned = false,
  /**
   * What has not been searched yet and may not be dropped into. Only the open
   * container's own grids; a box inside is unreachable anyway.
   */
  containerLock: DropLock = false,
): DropHover | null {
  // A gun leaving a weapon slot is never a part being fitted, and the target
  // slot usually holds another gun whose tile would swallow the drop; `reslot`
  // wants it.
  const reslotting = drag.from.t === "slot" && isWeaponEquipSlot(drag.from.slot);
  const weaponTarget = reslotting ? null : weaponTargetAt(drag.px, drag.py);
  if (weaponTarget !== null && weaponTarget.weaponIid !== drag.iid) {
    // Cartridges on a gun are a magazine being filled, wherever on the gun
    // they land — there is exactly one thing that can mean.
    if (def.kind === "ammo") {
      return loadDropHover(inv, container, weaponTarget.weaponIid, def);
    }
    const hover = weaponDropHover(
      inv,
      weaponTarget.weaponIid,
      def,
      weaponTarget.slot,
      container,
      weaponTarget.hostIid,
    );
    // Moving a fitted part directly between guns would need an atomic
    // detach+attach op. Keep the target honestly red until such an op exists.
    return drag.from.t === "mod" ? { ...hover, ok: false } : hover;
  }
  const containerTarget = containerTargetAt(drag.px, drag.py);
  if (containerTarget !== null) {
    return containerDropHover(inv, container, containerTarget, drag, def);
  }
  const zone = zoneAt(drag.px, drag.py);
  if (zone === null) return null;

  if (zone.t === "slot") {
    // Two gestures land on a slot: a loose item is equipped (a box counts as a
    // grid), a worn gun is moved by `reslot`. Back onto its own slot is a
    // cancel.
    if (drag.from.t === "slot") {
      const from = drag.from.slot;
      const ok =
        isWeaponEquipSlot(from) &&
        isWeaponEquipSlot(zone.slot) &&
        zone.slot !== from &&
        canEquipInSlot(def, zone.slot) &&
        // An occupied destination is a swap only if the gun there can go the
        // other way.
        occupantYields(inv, zone.slot, from);
      return { t: "slot", slot: zone.slot, ok };
    }
    // A body slot counts as a loose source: his helmet straight onto your head
    // is one `equip`. The doll's wells are never a destination.
    const ok =
      (drag.from.t === "grid" || drag.from.t === "box" || drag.from.t === "bodySlot") &&
      canEquipInSlot(def, zone.slot) &&
      inv.equipment[zone.slot] === null;
    return { t: "slot", slot: zone.slot, ok };
  }

  // The destination, as one value: a top-level area, or a box's contents grid
  // with everything the nesting rules will want to ask about it.
  let resolved:
    | { kind: "area"; area: AreaId; grid: GridState }
    | { kind: "box"; box: BoxTarget; grid: GridState }
    | null = null;
  if (zone.t === "box") {
    const box = boxAt(inv, container, zone.boxIid);
    if (box !== null) resolved = { kind: "box", box, grid: box.grid };
  } else {
    const areaGrid = gridOfArea(inv, container, zone.area);
    if (areaGrid !== null) resolved = { kind: "area", area: zone.area, grid: areaGrid };
  }
  if (resolved === null) return null;
  const target = resolved;
  const grid = target.grid;

  /** One hover over this destination, addressed the way the wire will address it. */
  const cell = (x: number, y: number, ok: boolean, ontoIid: string | null): DropHover =>
    target.kind === "box"
      ? { t: "cell", area: "inside", boxIid: target.box.item.iid, x, y, ok, ontoIid }
      : { t: "cell", area: target.area, x, y, ok, ontoIid };

  // The rect is in viewport pixels, CELL in logical pixels — derive the
  // rendered cell size from the rect. The item goes where the ghost IS (cursor
  // minus grab offset, snapped), not the cursor's cell minus half a footprint,
  // which biases even-sized items up and left.
  const renderedCellW = zone.rect.width / grid.w;
  const renderedCellH = zone.rect.height / grid.h;
  const fx = (drag.px - zone.rect.left) / renderedCellW;
  const fy = (drag.py - zone.rect.top) / renderedCellH;
  const x = Math.round(fx - drag.grabX / CELL) + 0; // `+ 0`: never -0, which deep-equals nothing
  const y = Math.round(fy - drag.grabY / CELL) + 0;
  /** The cursor's own cell — what the player is pointing AT (see merge below). */
  const cx = Math.floor(fx);
  const cy = Math.floor(fy);

  // Before the merge test: a merge is a drop into the container too, and a
  // same-def stack would paint green over a crate not yet searched.
  if (target.kind === "area" && target.area === "container") {
    if (locked(containerLock, { area: "container" })) return cell(x, y, false, null);
  } else if (target.kind === "box") {
    if (locked(containerLock, { area: "inside", boxIid: target.box.item.iid })) {
      return cell(x, y, false, null);
    }
  }

  // Merge beats move, tested against the CURSOR's cell. Works inside a box for
  // free: `merge` names two instances and never a cell.
  const onto = itemAtCell(grid, cx, cy);
  if (
    onto !== null &&
    onto.item.iid !== drag.iid &&
    onto.item.defId === drag.defId &&
    def.stackMax > 1 &&
    onto.item.stack < def.stackMax
  ) {
    return cell(x, y, true, onto.item.iid);
  }

  // Cartridges on a magazine in the grid go INTO it. Below the merge test
  // (ammo is not a magazine, so the two never match the same occupant), above
  // placement (never a request to shove the magazine aside).
  if (def.kind === "ammo" && onto !== null && isTrueMagazine(getItemDef(onto.item.defId))) {
    return loadDropHover(inv, container, onto.item.iid, def);
  }

  // A bag cannot be unequipped into itself or a box inside it — `unequip`
  // clears the slot before resolving, so both come back "no-area".
  if (drag.from.t === "slot") {
    const intoItself =
      target.kind === "box" ? target.box.area === drag.from.slot : target.area === drag.from.slot;
    if (intoItself) return cell(x, y, false, null);
  }

  // The two nesting checks in `placementGate`'s order. The dragged instance is
  // looked up, not assumed empty — a medbox full of splints brings a level with
  // it.
  if (target.kind === "box") {
    const dragged = instanceAt(inv, container, drag.from, drag.iid);
    const subtree = dragged === null ? 0 : subtreeDepth(wrapped(inv, drag.from, dragged));
    const legal =
      containerAccepts(target.box.def, def) &&
      target.box.depth + subtree <= MAX_GRID_DEPTH &&
      // The chain runs outermost-inward and ends with the box itself, so one
      // membership test covers "into itself" and "into something already in it".
      !target.box.chain.includes(drag.iid);
    if (!legal) return cell(x, y, false, null);
  }

  // `ignoreIid` ONLY where the item actually is; elsewhere it would excuse a
  // different item that happened to share the id.
  const sameGrid =
    target.kind === "box"
      ? drag.from.t === "box" && drag.from.boxIid === target.box.item.iid
      : drag.from.t === "grid" && drag.from.area === target.area;
  const ok = canPlace(grid, def, x, y, drag.rot, sameGrid ? drag.iid : undefined);
  return cell(x, y, ok, null);
}

// ---------------------------------------------------------------------------
// Release
// ---------------------------------------------------------------------------

/** The op a release would perform, or `null` for "this was a cancel". */
export function dropOp(drag: DragState): InventoryOp | null {
  const h = drag.hover;
  if (h === null || !h.ok) return null;

  // Screen-specific sinks are handled by `useInventoryDrag`; they are not
  // inventory operations and must never leak onto the wire as one.
  if (h.t === "action") return null;

  if (h.t === "weapon") {
    if (h.slot === null || (drag.from.t !== "grid" && drag.from.t !== "box")) return null;
    // `hostIid` is spread rather than always written, so a drop on a gun's own
    // slot still produces the byte-identical pre-v18 payload.
    return {
      t: "attach",
      iid: drag.iid,
      weaponIid: h.weaponIid,
      slot: h.slot,
      ...(h.hostIid === undefined ? {} : { hostIid: h.hostIid }),
    };
  }

  // Pouring rounds in: the two iids are the whole op. Built from what the
  // hover PROVED — only a stack in a grid or a box can be poured.
  if (h.t === "load") {
    if (drag.from.t !== "grid" && drag.from.t !== "box") return null;
    return { t: "load", iid: drag.iid, ontoIid: h.ontoIid };
  }

  if (h.t === "container") {
    if (h.to === null) return null;
    if (drag.from.t === "mod") {
      return {
        t: "detach",
        weaponIid: drag.from.weaponIid,
        slot: drag.from.slot,
        to: h.to,
        ...(drag.from.hostIid === undefined ? {} : { hostIid: drag.from.hostIid }),
      };
    }
    if (drag.from.t === "slot") return { t: "unequip", slot: drag.from.slot, to: h.to };
    // A body slot included: the wire op is a plain `move` (the server LOCATES
    // the item, and the body's slots are among the places it looks).
    return { t: "move", iid: drag.iid, to: h.to };
  }

  if (h.t === "slot") {
    // A worn gun on another weapon slot is a `reslot` — never through a grid.
    // The tests restate `computeHover`'s conclusion: an op is built from what
    // the hover proved.
    if (drag.from.t === "slot") {
      const from = drag.from.slot;
      if (!isWeaponEquipSlot(from) || !isWeaponEquipSlot(h.slot)) return null;
      return { t: "reslot", from, to: h.slot };
    }
    if (drag.from.t !== "grid" && drag.from.t !== "box" && drag.from.t !== "bodySlot") {
      return null;
    }
    return { t: "equip", iid: drag.iid, slot: h.slot };
  }
  if (h.ontoIid !== null) return { t: "merge", iid: drag.iid, ontoIid: h.ontoIid };

  const to: TargetRef =
    h.area === "inside"
      ? { area: "inside", iid: h.boxIid, x: h.x, y: h.y, rot: drag.rot }
      : { area: h.area, x: h.x, y: h.y, rot: drag.rot };
  if (drag.from.t === "mod") {
    return {
      t: "detach",
      weaponIid: drag.from.weaponIid,
      slot: drag.from.slot,
      to,
      ...(drag.from.hostIid === undefined ? {} : { hostIid: drag.from.hostIid }),
    };
  }
  if (drag.from.t === "slot") return { t: "unequip", slot: drag.from.slot, to };

  // A click (press and release in the same cell of the same grid) must not
  // cost an op or rate-limit budget. A box source's grid is an id, not an
  // area. A body slot is never "where it was".
  const backWhereItWas =
    drag.from.t === "box"
      ? h.area === "inside" && h.boxIid === drag.from.boxIid
      : drag.from.t === "grid" && h.area === drag.from.area;
  if (
    backWhereItWas &&
    drag.origin.x === h.x &&
    drag.origin.y === h.y &&
    drag.origin.rot === drag.rot
  ) {
    return null;
  }
  return { t: "move", iid: drag.iid, to };
}

/**
 * The green/red ring a tile wears as a drop target — for the two hovers that
 * name their target by iid (`weapon`, `load`), on grid tiles and doll slots
 * alike. `container` has its own prop, since a placement can fail for reasons
 * a lid cannot express. `undefined` is what `TileBody` reads as no outline.
 */
export function tileDropOutline(
  drag: DragState | null,
  iid: string,
): "valid" | "invalid" | undefined {
  const h = drag?.hover ?? null;
  if (h === null) return undefined;
  const targeted =
    h.t === "weapon" ? h.weaponIid === iid : h.t === "load" ? h.ontoIid === iid : false;
  if (!targeted) return undefined;
  return h.ok ? "valid" : "invalid";
}

/**
 * R while dragging: the item turns about the cursor, anchor re-centred on the
 * rotated footprint.
 */
export function toggleRotation(drag: DragState): DragState {
  const def = getItemDef(drag.defId);
  if (def === null) return drag;
  const rot: Rotation = drag.rot === 0 ? 1 : 0;
  const c = centreGrab(footprint(def, rot));
  return { ...drag, rot, grabX: c.x, grabY: c.y };
}

/**
 * The drag anchor: the centre of the footprint, not where the pointer landed —
 * an item is always carried by its middle.
 */
export function centreGrab(f: { w: number; h: number }): { x: number; y: number } {
  return { x: (f.w * CELL) / 2, y: (f.h * CELL) / 2 };
}

// ---------------------------------------------------------------------------
// Auto-placement (the context menu's "where does this go?")
// ---------------------------------------------------------------------------

/**
 * First legal cell for `def` in canonical scan order, optionally trying `first`
 * ahead. The open container is only considered as `first` — taking a vest off
 * must never post it into somebody else's loot pile.
 */
export function firstFreeSpot(
  inv: Inventory,
  container: OpenContainer | null,
  def: ItemDef,
  first?: AreaId,
): TargetRef | null {
  const areas: AreaId[] = first === undefined ? [] : [first];
  for (const a of GRID_AREAS) {
    if (a !== first) areas.push(a);
  }
  for (const area of areas) {
    const grid = gridOfArea(inv, container, area);
    if (grid === null) continue;
    const spot = findFreePlacement(grid, def);
    if (spot !== null) return { area, x: spot.x, y: spot.y, rot: spot.rot };
  }
  return null;
}

/**
 * Where a worn item may be put down — `firstFreeSpot` minus its own capacity
 * grid: `unequip` clears the slot before resolving, so `{ area: "rig" }` names
 * a grid that no longer exists ("no-area"), and a full bag's grid is exactly
 * the one with room-shaped holes. The open container is never a candidate.
 */
export function unequipSpot(
  inv: Inventory,
  container: OpenContainer | null,
  slot: EquipSlot,
  def: ItemDef,
): TargetRef | null {
  const own = slot === "rig" || slot === "backpack" ? slot : null;
  for (const area of GRID_AREAS) {
    if (area === own) continue;
    const grid = gridOfArea(inv, container, area);
    if (grid === null) continue;
    const spot = findFreePlacement(grid, def);
    if (spot !== null) return { area, x: spot.x, y: spot.y, rot: spot.rot };
  }
  return null;
}
